export const translations = {
  en: {
    nav: {
      platform: "Platform",
      useCases: "Use Cases",
      industries: "Industries",
      company: "Company",
      pilot: "Pilot Program",
      investors: "Investors",
      contact: "Contact",
      cta: "Request a discovery session",
    },
    common: {
      applyPilot: "Apply as a pilot partner",
      discoverPlatform: "Explore the platform",
      viewDemo: "Explore the interactive simulation",
      talkUseCase: "Let's talk about your use case",
      inDevelopment: "Dynamis is under development",
      selectingPartners: "We are selecting pilot partners",
    },
    home: {
      metaTitle: "Dynamis — Supply Chain Decision Intelligence",
      metaDesc: "Dynamis connects data, systems and operational constraints, simulates alternative scenarios and helps management identify the most effective action.",
      eyebrow: "Supply Chain Decision Intelligence",
      title: "Turn supply chain data into better decisions.",
      subtitle: "Dynamis connects data, systems and operational constraints, simulates alternative scenarios and helps management identify the most effective action before a problem becomes a cost.",
      problemTitle: "The data exists. The decisions stay fragmented.",
      problemBody: "Companies have ERPs, dashboards and large amounts of data, yet many critical decisions are still made through spreadsheets, manual analysis, meetings and unsynchronised information.",
      problems: [
        { t: "Scattered data", d: "Information distributed across systems and functions, hard to connect into a coherent picture." },
        { t: "Reporting tools", d: "Tools built for reporting, not for choosing the action to take." },
        { t: "Slow decisions", d: "Cross-functional decisions that are slow, hard to explain and hard to measure." },
      ],
      problemClose: "The problem is not only knowing what is happening. It is understanding what is worth doing now.",
      cycleTitle: "How it works",
      cycleIntro: "A continuous loop from raw data to an executed, measured decision.",
      cycle: [
        { k: "Sense", d: "Integrate data, events, constraints and signals from company systems into a single, up-to-date representation." },
        { k: "Simulate", d: "Understand the causes of a problem and simulate alternative scenarios to anticipate their effects on the network." },
        { k: "Solve", d: "Identify the best alternative by combining artificial intelligence and mathematical optimisation." },
        { k: "Execute", d: "Approve, track and support the execution of the decision, keeping humans in control." },
      ],
      featuresTitle: "One platform, many capabilities.",
      featuresIntro: "Dynamis combines multiple technologies into a single decision intelligence layer.",
      features: [
        { t: "Supply Chain Digital Twin", d: "A dynamic, queryable representation of the entire supply network." },
        { t: "Data Integration Layer", d: "Connection to ERP, WMS, TMS, MES, data lakes, databases and Excel files." },
        { t: "AI & multi-agent systems", d: "Specialised agents collaborating on demand, capacity, inventory and logistics." },
        { t: "What-if simulations", d: "Comparison of alternative scenarios before a decision is made." },
        { t: "Root-cause analysis", d: "Identification of the deep causes of delays and inefficiencies." },
        { t: "Mathematical optimisation", d: "Optimal solutions across cost, service, capacity and risk." },
        { t: "Conversational assistant", d: "Query the supply chain in natural language." },
        { t: "Executive Decision Board", d: "A concise view of open decisions and their impact." },
        { t: "Human-in-the-Loop", d: "Critical decisions always remain under human supervision." },
        { t: "Decision Memory", d: "Traceability of decisions, rationale, results and impact over time." },
      ],
      valueTitle: "The value Dynamis aims to produce.",
      valueIntro: "Potential outcomes enabled by faster, coordinated and measurable decisions.",
      values: [
        "Faster, coordinated decisions across functions",
        "Reduction of logistics and operating costs",
        "Better use of production capacity",
        "Optimisation of inventory and working capital",
        "Improvement of SLA, OTIF and operational continuity",
        "Early identification of risks and bottlenecks",
        "Greater transparency and traceability of decisions",
      ],
      integrationTitle: "On top of existing systems, not in their place.",
      integrationBody: "Dynamis does not replace company systems. It operates as a decision intelligence layer above them and turns their data into coordinated decisions.",
      externalSources: "External sources",
      network: {
        ariaLabel: "Supply chain network: an event propagates while Dynamis evaluates an alternative route",
        supplier: "Supplier", factory: "Plant", warehouse: "Warehouse",
        transport: "Transport", customer: "Customer", event: "Event detected",
        alternative: "Alternative evaluated", network: "Network",
      },
      videoTitle: "See Dynamis in action",
      videoBody: "An overview of the platform and of the Sense → Simulate → Solve → Execute loop.",
      pilotTitle: "Let's build the first use case on your company's real data.",
      pilotIntro: "Dynamis is selecting a limited number of companies with complex supply chains to develop the first pilot projects.",
      industriesTitle: "Industries",
      industriesIntro: "Designed for industrial contexts with complex supply networks.",
      closingTitle: "The next generation of supply chains will not just observe. It will decide better.",
      closingBody: "Bring Dynamis a complex decision, a fragmented process or a high-impact use case. Together we will assess whether there are enough data, conditions and value to develop a pilot project.",
    },
    platform: {
      metaTitle: "Platform — Dynamis",
      metaDesc: "Conceptual architecture, digital twin, multi-agent orchestration, simulations, optimisation, explainability and Decision Memory.",
      eyebrow: "The platform",
      title: "A decision intelligence layer on top of your supply chain.",
      intro: "Dynamis builds a dynamic representation of the supply network and uses it to simulate, optimise and support operational and strategic decisions.",
      sections: [
        { t: "Conceptual architecture", d: "A decision intelligence layer that sits above transactional systems and orchestrates them, without replacing them." },
        { t: "Digital twin", d: "A dynamic, queryable representation of plants, suppliers, warehouses, transport and customers, with their constraints." },
        { t: "Multi-agent orchestration", d: "Specialised agents collaborate on demand, capacity, inventory, logistics and risk, coordinating toward common goals." },
        { t: "What-if simulations", d: "Alternative scenarios compared before the decision, to anticipate effects and trade-offs on the network." },
        { t: "Mathematical optimisation", d: "Optimal solutions across cost, service, capacity, inventory and margin, within constraints." },
        { t: "Explainability", d: "Explainable recommendations: management understands why one alternative is preferable to another." },
        { t: "Human supervision", d: "Critical decisions remain under human control. Dynamis supports, it does not replace managerial judgement." },
        { t: "Decision Memory", d: "Recording of decisions, rationale, results and impact over time, to learn and improve." },
        { t: "Progressive integration", d: "A limited data perimeter at first, extended in later phases as value is demonstrated." },
        { t: "Data security & separation", d: "Architecture designed for data separation between customers. Security measures are being defined." },
      ],
      note: "Dynamis is under development. Some of the described capabilities are being released progressively.",
    },
    useCases: {
      metaTitle: "Use Cases — Dynamis",
      metaDesc: "Nine supply chain decision intelligence use cases: problem, supported decision and KPIs potentially affected.",
      eyebrow: "Use Cases",
      title: "Complex decisions, made faster and measurable.",
      intro: "For each use case we outline the problem, the supported decision and the KPIs potentially affected.",
      labels: { problem: "Problem", decision: "Supported decision", kpi: "KPIs potentially affected" },
      items: [
        { t: "Product design decisions", problem: "Product, material and component alternatives must be evaluated before industrialisation.", decision: "How to compare product configurations across performance, manufacturability, cost and supply risk.", kpi: "Industrial cost, time to market, manufacturability, supply risk" },
        { t: "Balancing demand, capacity and availability", problem: "Variable demand against limited capacity and availability.", decision: "How to align demand, capacity and availability plans in the short and medium term.", kpi: "Service level, capacity utilisation, overtime cost" },
        { t: "Production planning and constraint management", problem: "Production constraints hard to combine manually.", decision: "Which production sequence and allocation respects constraints while minimising cost.", kpi: "OTIF, plant utilisation, unit cost" },
        { t: "Inventory optimisation", problem: "Inventory too high in some points, stock-outs in others.", decision: "Where and how much to stock to balance service and working capital.", kpi: "Inventory turns, tied-up capital, stock-out" },
        { t: "Order and material allocation", problem: "Materials and orders contended across needs.", decision: "How to allocate orders and materials across sites, lines and customers.", kpi: "Fill rate, customer priority, margin" },
        { t: "Response to disruption and supplier risk", problem: "Unexpected events propagating through the network.", decision: "Which mitigation action to take and with what impact.", kpi: "Operational continuity, cost of disruption, response time" },
        { t: "Logistics and distribution optimisation", problem: "Complex distribution networks and high transport costs.", decision: "How to route flows and vehicles minimising cost and time.", kpi: "Transport cost, lead time, vehicle utilisation" },
        { t: "Root-cause analysis of delays and inefficiencies", problem: "Recurring delays with unknown cause.", decision: "Which lever to act on to remove the deep cause.", kpi: "On-time performance, cycle time, rework" },
        { t: "Economic impact assessment of alternatives", problem: "Difficulty comparing alternatives economically.", decision: "Which alternative delivers the best overall economic outcome.", kpi: "Margin, total cost, expected return" },
      ],
    },
    industries: {
      metaTitle: "Industries — Dynamis",
      metaDesc: "Manufacturing, Consumer Goods & Retail, Pharma & Life Sciences, Food & Beverage, Fashion, Logistics & 3PL.",
      eyebrow: "Industries",
      title: "Built for complex industrial supply chains.",
      intro: "Dynamis is designed for contexts with multiple constraints, extended networks and high economic-impact decisions.",
      items: [
        { t: "Manufacturing", d: "Production planning, constraint management and capacity utilisation." },
        { t: "Consumer Goods & Retail", d: "Balancing demand, inventory and service level across extended networks." },
        { t: "Pharma & Life Sciences", d: "Supply continuity, traceability and management of regulatory constraints." },
        { t: "Food & Beverage", d: "Perishability, seasonality and optimisation of inventory and logistics." },
        { t: "Fashion", d: "Short cycles, high variability and allocation of products and materials." },
        { t: "Logistics & 3PL", d: "Optimisation of distribution networks, vehicles and flows across customers." },
      ],
    },
    company: {
      metaTitle: "Company — Dynamis",
      metaDesc: "The Dynamis mission: making complex decisions faster, measurable, explainable and consistent with business goals.",
      eyebrow: "Company",
      title: "Making complex decisions better.",
      missionLabel: "Our mission",
      mission: "Making complex decisions faster, measurable, explainable and consistent with business goals.",
      founderLabel: "Founder",
      founderName: "Silvio Ciabarra",
      founderRole: "Founder",
      linkedinLabel: "LinkedIn profile",
      founderBio: "Executive and transformation leader in management and technology consulting, in running complex programmes and in delivering transformation and innovation initiatives for large organisations.",
      teamNote: "The Dynamis team is growing. New members will be introduced in this section.",
    },
    pilot: {
      metaTitle: "Pilot Program — Dynamis",
      metaDesc: "A structured five-phase path to build the first use case on your company's real data.",
      eyebrow: "Pilot Program",
      title: "Let's build the first use case on your company's real data.",
      intro: "Dynamis is selecting a limited number of companies with complex supply chains. The path is structured and low-risk.",
      steps: [
        { t: "Discovery", d: "Discovery and selection of the priority problem, high-impact and with data available." },
        { t: "Business case", d: "Definition of the business case and the KPIs to measure results against." },
        { t: "Data", d: "Connection of a limited data perimeter, sufficient for the selected use case." },
        { t: "Proof of Concept", d: "Proof of Concept and scenario simulation based on real data." },
        { t: "Measurement", d: "Measurement of results and definition of a phased extension plan." },
      ],
    },
    contact: {
      metaTitle: "Contact — Dynamis",
      metaDesc: "Request a discovery session or apply as a Dynamis pilot partner.",
      eyebrow: "Contact",
      title: "Let's talk about your use case.",
      intro: "Fill in the form: we will get back to you to assess together whether there are enough data, conditions and value for a pilot project.",
      fields: {
        fullName: "Full name",
        company: "Company",
        role: "Role",
        email: "Business email",
        industry: "Industry",
        companySize: "Approximate company size",
        message: "Problem or decision you want to address",
        interest: "Interest",
        privacy: "I have read the Privacy Policy and consent to the processing of my data.",
      },
      placeholders: {
        select: "Select…",
        message: "Briefly describe the decision or process you want to work on…",
      },
      interests: { discovery: "Discovery session", demo: "Platform demo", pilot: "Pilot program", partnership: "Partnership" },
      sizes: ["< 50 employees", "50–250 employees", "250–1,000 employees", "1,000–5,000 employees", "> 5,000 employees"],
      submit: "Send request",
      sending: "Sending…",
      successTitle: "Request sent",
      successBody: "Thank you. We have received your request and will get back to you soon.",
      newRequest: "Send another request",
      errors: {
        required: "Required field",
        email: "Enter a valid email",
        privacy: "Privacy consent is required",
        generic: "Something went wrong. Please try again.",
      },
    },
    privacy: {
      metaTitle: "Privacy Policy — Dynamis",
      metaDesc: "Privacy policy of Dynamis Decision Intelligence S.r.l.",
      title: "Privacy Policy",
      updated: "Last updated",
      body: [
        { t: "Data controller", d: "The data controller is Dynamis Decision Intelligence S.r.l. For any request you can write to info@dynamisdecisionintelligence.com." },
        { t: "Data collected", d: "We collect the data you enter in the contact form: full name, company, role, business email, industry, company size and the content of your message." },
        { t: "Purpose", d: "Data is used only to respond to requests, assess eligibility for a pilot project and for related communications." },
        { t: "Legal basis", d: "Processing is based on consent and on the legitimate interest in following up on requests received." },
        { t: "Retention", d: "Data is retained for as long as necessary for the stated purposes and in compliance with legal obligations." },
        { t: "Your rights", d: "You may request access, rectification, erasure, restriction and portability of your data, and object to processing, by writing to info@dynamisdecisionintelligence.com." },
        { t: "Cookies", d: "This site uses only essential technical cookies required for its operation. No profiling cookies or third-party tracking tools are used." },
      ],
    },
    footer: {
      tagline: "Supply Chain Decision Intelligence.",
      company: "Company",
      product: "Product",
      legal: "Legal",
      privacy: "Privacy Policy",
      rights: "All rights reserved.",
      status: "Dynamis is under development and is selecting pilot partners.",
    },
  },
};
